// "Alertas por módulo": um cartão por módulo que o usuário pode abrir. Três estados honestos: com pendências, sem pendências
// (dados reais) e indicador indisponível (o módulo ainda não expõe agregados). Nunca mostra número que não veio do módulo.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ModuleSummaryCard, type ModuleSummaryRow, type ModuleSummaryState } from '@giyus/patterns';
import { Card, Skeleton } from '@giyus/ui';

import type { NavItem } from '../shell/navItems';
import { LINK_CLASS } from './AttentionPanel';
import { toNumber } from './homeData';
import type { HomeScope, HomeSignals } from './useHomeSignals';

/** Módulos mostrados (ERP/Suppliers e Facilities seguem congelados até P5–P8). */
const ALERT_MODULES = [
  'workflow',
  'authz',
  'tenancy',
  'studies',
  'shared-mailboxes',
  'people',
  'identity',
  'documents',
  'scheduling',
  'notifications',
  'reports',
  'suppliers',
  'facilities',
  'settings',
  'platform-health',
] as const;
type AlertModuleKey = (typeof ALERT_MODULES)[number];

/** Módulos sem fonte persistida de dados (hoje em memória): o painel não mostra número algum deles. */
const NO_PERSISTED_DATA: ReadonlySet<string> = new Set(['documents', 'scheduling']);

interface CardModel {
  readonly state: ModuleSummaryState;
  readonly stateLabel: string;
  readonly rows: readonly ModuleSummaryRow[];
  readonly unavailableText?: string;
}

interface QueryLike {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly fetchStatus?: string;
}

/** Uma fonte desligada (`enabled: false`) fica `isPending` sem buscar; só conta como carregando se de fato busca. */
const loadingOf = (query: QueryLike): boolean => query.isPending && query.fetchStatus !== 'idle';

function Loading() {
  return (
    <Card className="gap-3 p-4" role="status" aria-busy="true">
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="h-4 w-3/4" />
    </Card>
  );
}

export interface ModuleAlertsProps {
  readonly scope: HomeScope;
  /** Itens de navegação visíveis (já filtrados pela permissão): definem quais módulos aparecem e para onde levam. */
  readonly navItems: readonly NavItem[];
  readonly signals: HomeSignals;
}

export function ModuleAlerts({ scope, navItems, signals }: ModuleAlertsProps) {
  const { t } = useTranslation('shell');
  const text = (key: string, values?: Record<string, unknown>): string =>
    t(`home.dashboard.modules.${key}` as never, values as never) as unknown as string;
  const nav = new Map(navItems.map((item) => [item.key, item]));
  const modules = ALERT_MODULES.filter((key) => nav.has(key));

  const row = (
    id: string,
    labelKey: string,
    value: number,
    tone?: 'danger' | 'warning',
  ): ModuleSummaryRow => ({
    id,
    label: text(`rows.${labelKey}`),
    value,
    ...(tone && value > 0
      ? {
          tone,
          toneLabel:
            tone === 'danger'
              ? t('home.dashboard.attention.overdue')
              : t('home.dashboard.attention.pending'),
        }
      : {}),
  });

  const attentionLabel = (count: number): string => text('attention', { count });
  const failed = (): CardModel => ({
    state: 'unavailable',
    stateLabel: text('unavailable'),
    rows: [],
    unavailableText: text('error'),
  });
  const unavailable = (key: string): CardModel => ({
    state: 'unavailable',
    stateLabel: text('unavailable'),
    rows: [],
    unavailableText: NO_PERSISTED_DATA.has(key)
      ? text('unavailableNoStore')
      : text('unavailableText'),
  });

  function model(key: AlertModuleKey): CardModel | 'loading' {
    switch (key) {
      case 'workflow': {
        if (signals.counters.isError) {
          return failed();
        }
        if (signals.counters.isPending) {
          return 'loading';
        }
        const total = toNumber(signals.counters.data.total);
        const overdue = toNumber(signals.counters.data.overdue);
        return {
          state: overdue > 0 ? 'attention' : 'ok',
          stateLabel:
            overdue > 0 ? attentionLabel(overdue) : total > 0 ? text('onTrack') : text('ok'),
          rows: [row('open', 'open', total), row('overdue', 'overdue', overdue, 'danger')],
        };
      }
      case 'authz': {
        const sources = [
          { q: signals.breakGlass, on: signals.access.breakGlass },
          { q: signals.accessReviews, on: signals.access.accessReviews },
          { q: signals.twoKey, on: signals.access.twoKey },
          { q: signals.delegations, on: signals.access.delegations },
        ].filter((source) => source.on);
        if (sources.length === 0) {
          return unavailable(key);
        }
        if (sources.some((source) => source.q.isError)) {
          return failed();
        }
        if (sources.some((source) => loadingOf(source.q))) {
          return 'loading';
        }
        const breakGlass = signals.breakGlass.data ?? [];
        const breakGlassOverdue = breakGlass.filter((event) => event.reviewOverdue).length;
        const reviews = (signals.accessReviews.data ?? []).filter(
          (item) => item.decidedAt === null,
        ).length;
        const delegations = signals.delegations.data?.length ?? 0;
        const twoKey = signals.twoKey.data?.length ?? 0;
        const rows: ModuleSummaryRow[] = [];
        if (signals.access.breakGlass) {
          rows.push(row('bg', 'breakGlassPending', breakGlass.length, 'warning'));
          rows.push(row('bgo', 'breakGlassOverdue', breakGlassOverdue, 'danger'));
        }
        if (signals.access.accessReviews) {
          rows.push(row('ar', 'accessReviews', reviews, 'warning'));
        }
        if (signals.access.twoKey) {
          rows.push(row('tk', 'twoKey', twoKey, 'warning'));
        }
        if (signals.access.delegations) {
          rows.push(row('dl', 'delegations', delegations, 'warning'));
        }
        const alerts = breakGlass.length + reviews + delegations + twoKey;
        return {
          state: alerts > 0 ? 'attention' : 'ok',
          stateLabel: alerts > 0 ? attentionLabel(alerts) : text('ok'),
          rows,
        };
      }
      case 'tenancy': {
        if (!signals.access.tenants) {
          return unavailable(key);
        }
        if (signals.tenants.isError) {
          return failed();
        }
        if (signals.tenants.isPending) {
          return 'loading';
        }
        const items = signals.tenants.data.items;
        const pending = items.filter((tenant) => tenant.bootstrapPending).length;
        return {
          state: pending > 0 ? 'attention' : 'ok',
          stateLabel: pending > 0 ? attentionLabel(pending) : text('ok'),
          rows: [
            row('total', 'tenantsTotal', toNumber(signals.tenants.data.total)),
            row('pending', 'bootstrapPending', pending, 'warning'),
          ],
        };
      }
      case 'studies':
      case 'shared-mailboxes': {
        if (!signals.access.studies) {
          return unavailable(key);
        }
        if (signals.studies.isError) {
          return failed();
        }
        if (signals.studies.isPending) {
          return 'loading';
        }
        const data = signals.studies.data;
        if (key === 'shared-mailboxes') {
          const count = toNumber(data.unassignedMailboxMessages);
          return {
            state: count > 0 ? 'attention' : 'ok',
            stateLabel: count > 0 ? attentionLabel(count) : text('ok'),
            rows: [row('unassigned', 'unassigned', count, 'warning')],
          };
        }
        const inProgress = data.dossiersByStatus
          .filter((entry) => entry.status !== 'Signed')
          .reduce((sum, entry) => sum + toNumber(entry.count), 0);
        return {
          state: 'ok',
          stateLabel: text('ok'),
          rows: [
            row('studies', 'studiesTotal', toNumber(data.totalStudies)),
            row('dossiers', 'dossiersInProgress', inProgress),
          ],
        };
      }
      default:
        return unavailable(key);
    }
  }

  if (modules.length === 0) {
    return null;
  }

  const link = (key: AlertModuleKey): ReactNode => {
    const item = nav.get(key);
    return item ? (
      <Link to={item.to} className={LINK_CLASS}>
        {text('open')}
      </Link>
    ) : null;
  };

  return (
    <section
      aria-labelledby="home-modules-title"
      className="flex flex-col gap-3"
      data-testid="home-module-alerts"
      data-scope={scope}
    >
      <div className="flex flex-col gap-1">
        <h2 id="home-modules-title" className="text-title font-semibold text-heading">
          {text('title')}
        </h2>
      </div>
      <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {modules.map((key) => {
          const result = model(key);
          const item = nav.get(key);
          const label = item ? t(item.labelKey) : key;
          return (
            <li key={key} data-testid={`home-module-${key}`} className="flex">
              {result === 'loading' ? (
                <Loading />
              ) : (
                <ModuleSummaryCard
                  className="w-full"
                  title={label}
                  state={result.state}
                  stateLabel={result.stateLabel}
                  rows={result.rows}
                  {...(result.unavailableText ? { unavailableText: result.unavailableText } : {})}
                  action={link(key)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
